import { useEffect, useMemo, useRef, useState } from "react";

// Phase-3, 2026-07-21: shift-draft localStorage TTL. 48 h is roughly the
// span between a cashier's last shift and their next likely login on the
// same device, comfortably long enough to survive a network drop or a
// forgotten tab, but short enough that a shared cashier phone doesn't
// accumulate stale drafts forever.
const DRAFT_TTL_MS = 48 * 60 * 60 * 1000;
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtINR, fmtNum, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast, Toaster } from "sonner";
import { Trash2, Plus, ChevronLeft, ChevronRight, Save, Check } from "lucide-react";
// v17.7 (2026-07-21) — BarcodeScanner and all camera/scan icons removed from
// Shift Entry per operator brief: "typing-only, no camera anywhere on Shift
// Entry — even the Lubricants barcode scan". All proof/scan work happens on
// the Upload page.
// v17.9 (2026-07-22) — Payment tile broken-image thumbnails and stale
// screenshot-proof helper text removed; "NO TXN" replaced by plain-English
// "Mark as Nil" toggle; Submit validation V1..V8 enforced client + server.
import { useAuth } from "@/context/AuthContext";
import RoleGuide from "@/components/RoleGuide";
import PinSubmitModal from "@/components/PinSubmitModal";

const SHIFT_LABELS_3 = ["SC-01", "SC-02", "SC-03"];
const SHIFT_LABELS_2 = ["SC-D", "SC-N"];

/**
 * Client-side V1..V8 submit validation — see brief item 1.4 (2026-07-22).
 * Backend runs the same rules; this exists to give friendly, jump-to-field
 * errors before we even attempt the network call. Returning [] means OK.
 *
 * Returns: [{ rule, page, message, field? }, ...]  — one entry per failure.
 */
function validateSubmitClient(ctx) {
  const fails = [];
  const {
    date, shiftCode, cashier, attendees, readings, payments, debtors,
    variance_fuels, variance_cng, varianceFuelsInput, varianceCngInput,
    cashTol, pms, nozzles: nzMaster, hasFuels, hasCng,
  } = ctx;

  // V1 — date + shift
  if (!date || !shiftCode) fails.push({ rule: "V1", page: 1, message: "Pick the date and shift." });

  // V2 — cashier
  if (!cashier || !cashier.trim()) fails.push({ rule: "V2", page: 1, message: "Select the cashier." });

  // V3 — at least one attendee
  if (!attendees || attendees.length === 0) fails.push({ rule: "V3", page: 1, message: "Add at least one attendee." });

  // V4 — at least one nozzle row with closing > 0
  const activeReadings = (readings || []).filter((r) => Number(r.closing) > 0 && r.nozzle_id);
  if (activeReadings.length === 0) {
    fails.push({ rule: "V4", page: 2, message: "Enter at least one nozzle reading, or mark nozzles not operational." });
  }

  // V5 — every row that has any value: closing >= opening
  (readings || []).forEach((r, i) => {
    if (!r.nozzle_id) return;
    if (Number(r.closing) > 0 && Number(r.closing) < Number(r.opening)) {
      const nz = (nzMaster || []).find((n) => n.nozzle_id === r.nozzle_id);
      fails.push({
        rule: "V5", page: 2,
        message: `Row ${i + 1} (${nz?.label || r.nozzle_id}): Closing is lower than opening — check for meter rollback.`,
        field: `reading-closing-${i}`,
      });
    }
  });

  // V6 — every payment mode × category is either amount>=0 typed or explicitly nil
  const untouched = [];
  (pms || []).forEach((pm) => {
    ["Fuels", "CNG"].forEach((cat) => {
      const row = (payments || []).find((p) => p.pm_id === pm.pm_id && p.category === cat);
      if (!row) return;
      const filled = row.nil || row.touched || Number(row.amount) > 0;
      if (!filled) untouched.push(`${pm.name} (${cat === "Fuels" ? "Fuel" : "CNG"})`);
    });
  });
  if (untouched.length) {
    fails.push({
      rule: "V6", page: 3,
      message: `${untouched.length} payment mode${untouched.length === 1 ? " is" : "s are"} neither filled nor marked Nil: ${untouched.join(", ")}.`,
    });
  }

  // V7 — cash reconciliation resolved
  if (hasFuels && Math.abs(Number(variance_fuels) || 0) > cashTol) {
    if (!varianceFuelsInput?.reason_code) {
      const dF = variance_fuels;
      const direction = dF > 0 ? "excess" : "short";
      fails.push({
        rule: "V7", page: 3,
        message: `Cash difference ₹${Math.abs(dF).toFixed(2)} ${direction} (fuels) is unexplained. Pick a variance reason to submit.`,
      });
    }
  }
  if (hasCng && Math.abs(Number(variance_cng) || 0) > cashTol) {
    if (!varianceCngInput?.reason_code) {
      const dC = variance_cng;
      const direction = dC > 0 ? "excess" : "short";
      fails.push({
        rule: "V7", page: 3,
        message: `Cash difference ₹${Math.abs(dC).toFixed(2)} ${direction} (CNG) is unexplained. Pick a variance reason to submit.`,
      });
    }
  }

  // V8 — debtor rows: each has debtor, amount, reason (fuel is inherent)
  (debtors || []).forEach((d, i) => {
    if (!d.debtor_id) fails.push({ rule: "V8", page: 3, message: `Debtor row ${i + 1}: select the debtor.` });
    if (!(Number(d.amount_rs) > 0)) fails.push({ rule: "V8", page: 3, message: `Debtor row ${i + 1}: enter the amount.` });
    if (!d.reason || !String(d.reason).trim()) fails.push({ rule: "V8", page: 3, message: `Debtor row ${i + 1}: reason is required.` });
  });

  return fails;
}

export default function ShiftEntryForm() {
  const { entryId } = useParams();
  const isEdit = !!entryId;
  const nav = useNavigate();
  const { user: authUser } = useAuth();
  const [page, setPage] = useState(1);

  // masters
  const [nozzles, setNozzles] = useState([]);
  const [pms, setPMs] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [lubs, setLubs] = useState([]);
  const [prices, setPrices] = useState({ HSD: 0, MS: 0, CNG: 0 });
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);  // Phase-3: attendee/cashier dropdown
  const [shiftMode, setShiftMode] = useState("3-shift");
  const [trialLitres, setTrialLitres] = useState(5);
  const [cashTol, setCashTol] = useState(10);  // ₹ tolerance band — see Settings › CASH_TOLERANCE (default 10). Within this band, variance auto-records as 'Rounding'; outside, a reason is required. Historically we had a stricter ₹2 setting; the user's Phase-3 A1 brief locked it at ±₹10.

  // form state
  const [date, setDate] = useState(todayISO());
  const [shiftCode, setShiftCode] = useState("SC-01");
  const [attendees, setAttendees] = useState([]);
  const [cashier, setCashier] = useState("");

  const [readings, setReadings] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [lubSales, setLubSales] = useState([]);
  const [payments, setPayments] = useState([]);
  const [debtorRows, setDebtorRows] = useState([]);

  const [cashFuels, setCashFuels] = useState(0);
  const [cashCng, setCashCng] = useState(0);
  // Phase-3 A1: cash variance is its OWN entity — stored on the entry
  // (variance_fuels / variance_cng) but never nets out the Difference. When
  // |Difference| > tolerance, reason_code is REQUIRED before submit.
  const [varianceFuels, setVarianceFuels] = useState({ reason_code: "", note: "" });
  const [varianceCng, setVarianceCng] = useState({ reason_code: "", note: "" });
  // Phase-3 A2: PIN modal state — opened on submit-non-draft; carries the
  // 4-digit PIN through to the /shifts endpoint for server-side re-verify.
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);   // holds the payload while user types PIN
  const [submitFailures, setSubmitFailures] = useState([]);   // V1..V8 client-side failures (see submit())

  const [preview, setPreview] = useState(null);
  const [hints, setHints] = useState({});
  // Phase-3.7: live readiness gate — reads /shifts/readiness on the same
  // debounce cycle as preview.  Never mutates state; just tells the operator
  // what is still missing so the Submit button can be gated.
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  // load masters
  useEffect(() => {
    (async () => {
      try {
        const [n, p, d, l, u, cfg, emp] = await Promise.all([
          api.get("/masters/nozzles"),
          api.get("/masters/payment_modes"),
          api.get("/masters/debtors"),
          api.get("/masters/lubricants"),
          api.get("/masters/users").catch(() => ({ data: [] })),
          api.get("/masters/config"),
          api.get("/payroll/employees", { params: { scope: "vas" } }).catch(() => ({ data: [] })),
        ]);
        setNozzles(n.data);
        const activePms = p.data.filter((x) => x.active !== false);
        setPMs(activePms);
        setDebtors(d.data.filter((x) => x.status === "Active"));
        setLubs(l.data.filter((x) => x.active !== false));
        setUsers(u.data || []);
        setEmployees((emp.data || []).filter((e) => e.active !== false));
        setShiftMode(cfg.data?.SHIFT_MODE || "3-shift");
        setTrialLitres(Number(cfg.data?.TRIAL_LITRES_PER_NOZZLE ?? 5));
        setCashTol(Number(cfg.data?.CASH_TOLERANCE ?? 10));
        // Auto-populate all payment modes × both categories at ₹0 if payments is empty
        // (this only fires on brand-new entries; editing an existing entry keeps its rows)
        setPayments((cur) => {
          if (cur.length > 0) return cur;
          const rows = [];
          for (const pm of activePms) {
            for (const cat of ["Fuels", "CNG"]) {
              rows.push({ pm_id: pm.pm_id, category: cat, amount: 0, misc_customer_name: "", payment_done_through: "", proof_file_id: null });
            }
          }
          return rows;
        });
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  // load prices for date
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/masters/fuel_prices/for/${date}`);
        setPrices({ HSD: r.data.hsd, MS: r.data.ms, CNG: r.data.cng });
      } catch (err) {
        // No price row yet for this date — owner sets it before shift starts.
        console.debug("fuel_prices lookup failed", date, err?.response?.status);
      }
    })();
  }, [date]);

  // load existing entry
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api.get(`/shifts/${entryId}`);
        const e = r.data;
        setDate(e.date);
        setShiftCode(e.shift_code);
        setAttendees(e.attendee_names || []);
        setCashier(e.cashier_name || "");
        setReadings(e.readings || []);
        setAdjustments(e.adjustments || []);
        setLubSales(e.lubricants || []);
        setPayments(e.payments || []);
        setDebtorRows(e.debtors || []);
        setCashFuels(e.cash_in_hand_fuels || 0);
        setCashCng(e.cash_in_hand_cng || 0);
        setVarianceFuels(e.variance_fuels || { reason_code: "", note: "" });
        setVarianceCng(e.variance_cng || { reason_code: "", note: "" });
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [entryId, isEdit]);

  const shiftOptions = shiftMode === "2-shift" ? SHIFT_LABELS_2 : SHIFT_LABELS_3;

  // Draft autosave — stores the cashier's in-progress shift entry to
  // localStorage so a flaky network or accidental page close doesn't lose
  // the readings/payments they typed. Contents are NON-SENSITIVE (their own
  // shift's fuel litres + amounts); no auth tokens, no PII, no payment
  // instruments. Draft is cleared on Submit (see save() below). This is why
  // localStorage — which persists across tabs on the same device — is the
  // right choice over sessionStorage (would lose the draft when the cashier
  // switches tabs) and IndexedDB (overkill for a ~5 KB blob).
  // Hygiene (Phase-3, 2026-07-21): drafts get a 48-hour TTL so a shared
  // cashier phone doesn't accumulate stale blobs indefinitely. Sweep runs
  // once on mount; the current draft is exempt because we're actively
  // editing it. `DRAFT_TTL_MS` is a module-level constant, no need in deps.
  const draftKey = `shift_draft_${date}_${shiftCode}`;
  useEffect(() => {
    try {
      const now = Date.now();
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith("shift_draft_") || k === draftKey) continue;
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const age = now - (Number(parsed?._savedAt) || 0);
          if (!parsed?._savedAt || age > DRAFT_TTL_MS) localStorage.removeItem(k);
        } catch { localStorage.removeItem(k); }  // corrupt blob → drop
      }
    } catch (err) { console.debug("draft TTL sweep failed", err); }
  }, [draftKey]);

  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(() => {
      const draft = { _savedAt: Date.now(), date, shiftCode, attendees, cashier, readings, adjustments, lubSales, payments, debtorRows, cashFuels, cashCng, varianceFuels, varianceCng };
      localStorage.setItem(draftKey, JSON.stringify(draft));
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 600);
    }, 500);
    return () => clearTimeout(t);
  }, [date, shiftCode, attendees, cashier, readings, adjustments, lubSales, payments, debtorRows, cashFuels, cashCng, varianceFuels, varianceCng, draftKey, isEdit]);

  // load draft — ONCE per (draftKey, isEdit) pair. We use a ref instead of
  // reading `attendees.length` inside the effect (which would create a
  // dependency loop with the autosave effect above).
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (draftLoadedRef.current) return;
    const raw = localStorage.getItem(draftKey);
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      if (d.attendees?.length) {
        setAttendees(d.attendees);
        setCashier(d.cashier || "");
        setReadings(d.readings || []);
        setAdjustments(d.adjustments || []);
        setLubSales(d.lubSales || []);
        setPayments(d.payments || []);
        setDebtorRows(d.debtorRows || []);
        setCashFuels(d.cashFuels || 0);
        setCashCng(d.cashCng || 0);
        setVarianceFuels(d.varianceFuels || { reason_code: "", note: "" });
        setVarianceCng(d.varianceCng || { reason_code: "", note: "" });
        draftLoadedRef.current = true;
      }
    } catch (err) {
      // Corrupt localStorage draft — clear so next load starts clean.
      console.debug("draft hydration failed", draftKey, err);
      try { localStorage.removeItem(draftKey); } catch (e2) { console.debug("draft cleanup failed", e2); }
    }
  }, [draftKey, isEdit]);

  // preview
  useEffect(() => {
    const t = setTimeout(async () => {
      const payload = {
        date, shift_code: shiftCode, attendee_names: attendees, cashier_name: cashier,
        readings: readings.map(sanitizeReading),
        adjustments: adjustments.map(sanitizeAdj),
        lubricants: lubSales.map(sanitizeLub),
        payments: payments.map(sanitizePay),
        debtors: debtorRows.map(sanitizeDeb),
        cash_in_hand_fuels: Number(cashFuels) || 0,
        cash_in_hand_cng: Number(cashCng) || 0,
        variance_fuels: sanitizeVariance(varianceFuels, preview?.difference_fuels),
        variance_cng: sanitizeVariance(varianceCng, preview?.difference_cng),
        draft: true,
      };
      try {
        const r = await api.post("/shifts/preview", payload);
        setPreview(r.data.totals);
        setHints(r.data.hints || {});
      } catch (e) {
        console.debug("preview failed", e?.response?.status);
      }
      // Readiness runs in parallel — never blocks preview.
      try {
        const rr = await api.post("/shifts/readiness", { ...payload, draft: false });
        setReadiness(rr.data);
      } catch (e) {
        console.debug("readiness failed", e?.response?.status);
      }
    }, 300);
    return () => clearTimeout(t);
    // NOTE: `preview` is deliberately excluded — including it would loop
    // (preview updates → effect fires → preview updates). `sanitizeVariance`
    // reads the latest preview via closure; over the 300ms debounce this
    // settles to the same value the effect would have re-computed anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, shiftCode, attendees, cashier, readings, adjustments, lubSales, payments, debtorRows, cashFuels, cashCng, varianceFuels, varianceCng]);

  async function fetchLastClosing(nozzleId) {
    try {
      const r = await api.get(`/shifts/nozzle/${nozzleId}/last_closing`, { params: { before_date: date, before_shift: shiftCode } });
      return r.data.last_closing;
    } catch (err) {
      console.debug("last_closing lookup failed for", nozzleId, err?.response?.status);
      return null;
    }
  }

  const hasFuels = readings.some((r) => {
    const nz = nozzles.find((n) => n.nozzle_id === r.nozzle_id);
    return nz && (nz.fuel === "HSD" || nz.fuel === "MS");
  });
  const hasCng = readings.some((r) => {
    const nz = nozzles.find((n) => n.nozzle_id === r.nozzle_id);
    return nz && nz.fuel === "CNG";
  });

  // Phase-3: memoise the two employee-master filters — otherwise they re-run
  // on every keystroke of every other field on the page.
  const attendeeCandidates = useMemo(
    () => employees.filter((emp) => !attendees.includes(emp.name)),
    [employees, attendees],
  );
  const cashierCandidates = useMemo(
    () => employees.filter(
      (emp) => emp.category === "Cashier" || (emp.designation || "").toLowerCase().includes("cashier"),
    ),
    [employees],
  );

  async function submit(draft = false) {
    const payload = {
      date, shift_code: shiftCode, attendee_names: attendees, cashier_name: cashier,
      readings: readings.map(sanitizeReading),
      adjustments: adjustments.map(sanitizeAdj),
      lubricants: lubSales.map(sanitizeLub),
      payments: payments.map(sanitizePay),
      debtors: debtorRows.map(sanitizeDeb),
      cash_in_hand_fuels: Number(cashFuels) || 0,
      cash_in_hand_cng: Number(cashCng) || 0,
      variance_fuels: sanitizeVariance(varianceFuels, preview?.difference_fuels),
      variance_cng: sanitizeVariance(varianceCng, preview?.difference_cng),
      draft,
    };
    if (!draft) {
      // ---- v17.9 SUBMIT VALIDATION (V1..V8) — matches operator brief 1.4.
      //      Client side runs to show friendly errors; backend runs the same
      //      checks (source of truth). See app/shifts.py validate_entry().
      const failures = validateSubmitClient({
        date, shiftCode, cashier, attendees, readings, payments, debtors: debtorRows,
        variance_fuels: preview?.difference_fuels || 0,
        variance_cng: preview?.difference_cng || 0,
        varianceFuelsInput: varianceFuels, varianceCngInput: varianceCng,
        cashTol: Number(cashTol) || 10, pms, nozzles,
        hasFuels, hasCng,
      });
      if (failures.length) {
        setSubmitFailures(failures);
        // Scroll to first failing page.
        const firstPage = failures[0].page || 1;
        setPage(firstPage);
        toast.error(`Cannot submit: ${failures.length} check${failures.length === 1 ? "" : "s"} failed.`);
        return;
      }
      setSubmitFailures([]);
      // Phase-3 A2: gate submit behind PIN.  Stash the payload and open the
      // modal — the actual POST fires in `finalizeSubmit` after PIN verify.
      setPendingSubmit(payload);
      setPinModalOpen(true);
      return;
    }
    // Draft path — no PIN needed, no validation.
    await finalizeSubmit(payload);
  }

  async function finalizeSubmit(payload) {
    setBusy(true);
    try {
      let r;
      if (isEdit) {
        r = await api.patch(`/shifts/${entryId}`, payload);
      } else {
        r = await api.post("/shifts/", payload);
      }
      localStorage.removeItem(draftKey);
      toast.success(payload.draft ? "Draft saved" : "Entry submitted");
      nav(`/shifts/${r.data.entry_id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  }

  async function handlePinVerified({ pin, name }) {
    setPinModalOpen(false);
    if (!pendingSubmit) return;
    await finalizeSubmit({ ...pendingSubmit, submitter_pin: pin });
    setPendingSubmit(null);
    if (name) toast.success(`Submitted as ${name}`);
  }

  return (
    <div data-testid="shift-form-root" className="space-y-4 pb-24">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Shift Entry</h1>
        <span data-testid="autosave-indicator" className={"text-xs px-2 py-1 rounded-sm border " + (savedTick ? "state-info" : "state-ok")}>
          {savedTick ? "Saving ⟳" : "Saved ✓"}
        </span>
      </div>

      <RoleGuide guides={{
        filler: { freq: "1× per shift", title: "You're punching a shift", text: "Fill nozzle readings on Page 2 by typing directly. Skip Payments/Cash: the cashier fills those. Proof photos go on the Upload screen later, not here." },
        cashier: { freq: "1× per shift", title: "You're closing the shift", text: "Page 2: confirm readings. Page 3: type amounts for each payment mode (₹0 is fine). Cash-in-hand → submit. Photo proofs live on the Upload screen — bulk-drop them after the shift." },
        assistant_manager: { freq: "as needed", title: "You're reviewing / correcting", text: "Verify the DRAFT the cashier submitted. Correct any suspect reading here; proof photos live on Upload." },
        manager: { freq: "as needed", title: "You're overseeing", text: "Same access as Supervisor plus you can override fuel prices and resolve HIGH-severity flags this creates." },
      }} />

      {/* V1..V8 submit failure banner — lists ALL failed rules with plain-
          English messages, per operator brief 1.4.  Rendered above the
          stepper so it's visible whichever tab you're on. */}
      {submitFailures.length > 0 && (
        <Card
          data-testid="submit-error-banner"
          className="border-2 border-red-500 bg-red-50 p-3 rounded-sm"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-bold text-red-800 mb-1">
                Can&rsquo;t submit yet — {submitFailures.length} check{submitFailures.length === 1 ? "" : "s"} failed
              </div>
              <ul className="text-sm text-red-900 space-y-0.5 list-disc ml-5">
                {submitFailures.map((f, i) => (
                  <li key={i} data-testid={`submit-error-${f.rule}-${i}`}>
                    <span className="font-mono text-xs mr-1 opacity-70">[{f.rule}]</span>
                    {f.message}
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setSubmitFailures([])}
              className="text-xs font-bold text-red-700 underline shrink-0"
              data-testid="dismiss-submit-errors"
            >
              Dismiss
            </button>
          </div>
        </Card>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((n) => (
          <button
            key={n}
            data-testid={`step-btn-${n}`}
            onClick={() => setPage(n)}
            className={"flex-1 h-10 border-2 text-sm font-bold rounded-sm " + (page === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")}
          >
            {n}. {n === 1 ? "Shift" : n === 2 ? "Sales" : "Payments"}
          </button>
        ))}
      </div>

      {page === 1 && (
        <Card className="p-4 space-y-4 border-2 border-slate-300 rounded-sm">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Date *</Label>
            <Input data-testid="entry-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-11 border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Shift *</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {shiftOptions.map((s) => (
                <button
                  key={s}
                  data-testid={`shift-btn-${s}`}
                  type="button"
                  onClick={() => setShiftCode(s)}
                  className={"px-4 py-2 border-2 font-bold text-sm rounded-sm " + (shiftCode === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Attendee Names *</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {attendees.map((a, i) => (
                <span key={a} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-300 rounded-sm px-2 py-1 text-sm" data-testid={`attendee-chip-${i}`}>
                  {a}
                  <button type="button" onClick={() => setAttendees(attendees.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-600">×</button>
                </span>
              ))}
            </div>
            {/* Phase-3 A3: dropdown-only from employees master. Free-text input
                is REMOVED — it guarantees typos (Munna Rai / Munna Ray) that
                fragment attendance and payroll. If the person isn't in the
                master, the admin has to add them there first. */}
            <div className="flex gap-2 mt-2">
              <select
                data-testid="attendee-select"
                value=""
                onChange={(e) => {
                  const name = e.target.value;
                  if (name && !attendees.includes(name)) setAttendees([...attendees, name]);
                }}
                className="h-11 flex-1 border border-slate-300 rounded-sm px-3 bg-white"
              >
                <option value="">— Add attendee from staff master —</option>
                {attendeeCandidates.map((emp) => (
                  <option key={emp.code} value={emp.name}>
                    {`${emp.name} · ${emp.category || emp.designation}`}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-slate-500 mt-1">If a staff member isn&rsquo;t on the list, ask the admin to add them in Employees — no free-text names.</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Cashier Name *</Label>
            <select
              data-testid="cashier-select"
              value={cashier}
              onChange={(e) => setCashier(e.target.value)}
              className="mt-1 w-full h-11 border border-slate-300 rounded-sm px-3 bg-white"
            >
              <option value="">— Select cashier —</option>
              {cashierCandidates.map((emp) => (
                <option key={emp.code} value={emp.name}>
                  {emp.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">Only staff marked as &quot;Cashier&quot; in the master appear here.</p>
          </div>
        </Card>
      )}

      {page === 2 && (
        <div className="space-y-4">
          <ReadingsSection
            readings={readings} setReadings={setReadings}
            nozzles={nozzles} prices={prices}
            fetchLastClosing={fetchLastClosing}
            shiftCode={shiftCode}
            trialLitres={trialLitres}
          />
          <AdjustmentsSection
            adjustments={adjustments} setAdjustments={setAdjustments}
            nozzles={nozzles} prices={prices}
          />
          <LubricantsSection
            lubSales={lubSales} setLubSales={setLubSales} lubs={lubs}
          />
        </div>
      )}

      {page === 3 && (
        <div className="space-y-4">
          <PaymentsSection payments={payments} setPayments={setPayments} pms={pms} currentUserEmail={authUser?.email} />
          <DebtorsSection debtorRows={debtorRows} setDebtorRows={setDebtorRows} debtors={debtors} prices={prices} date={date} shiftCode={shiftCode} />
          <CashBlock
            hasFuels={hasFuels} hasCng={hasCng}
            preview={preview} hints={hints}
            cashFuels={cashFuels} setCashFuels={setCashFuels}
            cashCng={cashCng} setCashCng={setCashCng}
            cashTol={cashTol}
            varianceFuels={varianceFuels} setVarianceFuels={setVarianceFuels}
            varianceCng={varianceCng} setVarianceCng={setVarianceCng}
          />
          <ReadyToSubmit readiness={readiness} setPage={setPage} />
        </div>
      )}

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-300 p-3 z-40">
        <div className="mx-auto max-w-[1400px] flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Button data-testid="prev-page-btn" variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="border-slate-300">
              <ChevronLeft size={14} /> Back
            </Button>
            <Button data-testid="next-page-btn" variant="outline" size="sm" onClick={() => setPage(Math.min(3, page + 1))} disabled={page === 3} className="border-slate-300">
              Next <ChevronRight size={14} />
            </Button>
          </div>
          <div className="hidden sm:block text-xs text-slate-600">
            {hasFuels && preview && <span className="mr-3">Fuels Δ <b className={Math.abs(preview.difference_fuels) > cashTol ? "text-red-600" : "text-emerald-700"}>{fmtINR(preview.difference_fuels)}</b></span>}
            {hasCng && preview && <span>CNG Δ <b className={Math.abs(preview.difference_cng) > cashTol ? "text-red-600" : "text-emerald-700"}>{fmtINR(preview.difference_cng)}</b></span>}
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="save-draft-btn" variant="outline" size="sm" onClick={() => submit(true)} disabled={busy} className="border-slate-300">
              <Save size={14} className="mr-1" /> Draft
            </Button>
            <Button data-testid="submit-entry-btn" size="sm" onClick={() => submit(false)}
                    disabled={busy}
                    className="bg-slate-900 hover:bg-slate-800 text-white disabled:bg-slate-400 disabled:cursor-not-allowed"
                    title={readiness && !readiness.ready ? `${readiness.reasons?.length || 0} things still to fill — click to see the full list.` : ""}>
              Submit Entry
              {readiness && !readiness.ready && (
                <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm" data-testid="submit-block-badge">
                  {readiness.reasons.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Phase-3 A2: PIN confirmation modal — final gate before submit. */}
      <PinSubmitModal
        open={pinModalOpen}
        onCancel={() => { setPinModalOpen(false); setPendingSubmit(null); }}
        onVerified={handlePinVerified}
      />
    </div>
  );
}

// -------- Sub-sections --------

function ReadingsSection({ readings, setReadings, nozzles, prices, fetchLastClosing, shiftCode, trialLitres }) {
  async function add() {
    setReadings([...readings, { _uid: rowUid(), nozzle_id: "", opening: 0, closing: 0, trial_litres: 0, price: 0 }]);
  }
  async function update(i, patch) {
    const next = [...readings];
    next[i] = { ...next[i], ...patch };
    if (patch.nozzle_id) {
      const nz = nozzles.find((n) => n.nozzle_id === patch.nozzle_id);
      next[i].price = nz ? prices[nz.fuel] : 0;
      const last = await fetchLastClosing(patch.nozzle_id);
      if (last != null) next[i].opening = last;
      // Trial auto-fills to Settings → TRIAL_LITRES_PER_NOZZLE (default 5 L)
      // on the FIRST shift of the day for HSD/MS. CNG never has a trial.
      const trialL = Number(trialLitres) || 5;
      if (nz && (nz.fuel === "HSD" || nz.fuel === "MS") && shiftCode === "SC-01") next[i].trial_litres = trialL;
      else if (nz && nz.fuel === "CNG") next[i].trial_litres = 0;
    }
    setReadings(next);
  }
  const del = (i) => setReadings(readings.filter((_, j) => j !== i));

  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-800">Sales — nozzle readings <span className="text-xs font-normal text-slate-500">(litres for MS/HSD · kg for CNG)</span></h3>
        <Button data-testid="add-reading-btn" type="button" variant="outline" size="sm" onClick={add} className="border-slate-300"><Plus size={14} /> Add</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="dense-table w-full text-sm">
          <thead>
            <tr>
              <th>Nozzle</th>
              <th>Closing (L / kg)</th>
              <th>Opening (L / kg)</th>
              <th>Gross (L / kg)</th>
              <th title="Daily 5 L accuracy check on every nozzle before 10:00. This much is dispensed into a can and poured back — never sold, so subtracted from sales."
                  className="cursor-help underline decoration-dotted">Trial (L)</th>
              <th>Net (L / kg)</th>
              <th>₹ / unit</th>
              <th>Net ₹</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {readings.map((r, i) => {
              const nz = nozzles.find((n) => n.nozzle_id === r.nozzle_id);
              const gross = Math.max(0, Number(r.closing) - Number(r.opening));
              const net = Math.max(0, gross - Number(r.trial_litres || 0));
              const netRs = net * Number(r.price || 0);
              const invalid = Number(r.closing) > 0 && Number(r.closing) <= Number(r.opening);
              return (
                <tr key={r._uid || `${r.nozzle_id || "empty"}-${i}`} data-testid={`reading-row-${i}`}>
                  <td>
                    <select
                      data-testid={`reading-nozzle-${i}`}
                      value={r.nozzle_id}
                      onChange={(e) => update(i, { nozzle_id: e.target.value })}
                      className="w-40 border border-slate-300 rounded-sm p-1"
                    >
                      <option value="">—</option>
                      {nozzles.filter((n) => n.active !== false).map((n) => (
                        <option key={n.nozzle_id} value={n.nozzle_id}>{n.label} ({n.fuel})</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Input
                        data-testid={`reading-closing-${i}`}
                        type="number" step="0.01"
                        value={r.closing}
                        onChange={(e) => update(i, { closing: Number(e.target.value) })}
                        className={"num w-28 h-9 border " + (invalid ? "border-red-500" : "border-slate-300")}
                      />
                      {/* Phase-3 (2026-07-21): the in-row "Scan meter" button
                          was removed here. It was accidentally broadcasting
                          the same OCR value into every nozzle row and
                          slowing down daily manual entry. OCR now runs
                          post-hoc in the Upload Inbox where we compare the
                          scanned reading against the typed one and flag
                          mismatches — see UploadInbox.jsx + meter_ocr.py. */}
                    </div>
                  </td>
                  <td>
                    <Input
                      data-testid={`reading-opening-${i}`}
                      type="number" step="0.01"
                      value={r.opening}
                      onChange={(e) => update(i, { opening: Number(e.target.value) })}
                      className="num w-28 h-9 border border-slate-300"
                    />
                  </td>
                  <td className="num">{fmtNum(gross)}</td>
                  <td>
                    <Input
                      data-testid={`reading-trial-${i}`}
                      type="number" step="0.01"
                      value={r.trial_litres}
                      onChange={(e) => update(i, { trial_litres: Number(e.target.value) })}
                      className="num w-16 h-9 border border-slate-300"
                      disabled={nz?.fuel === "CNG"}
                    />
                  </td>
                  <td className="num">{fmtNum(net)}</td>
                  <td className="num">{fmtNum(r.price)}</td>
                  <td className="num font-bold">{fmtINR(netRs)}</td>
                  <td><button type="button" onClick={() => del(i)} className="text-red-600"><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
            {readings.some((r) => Number(r.closing) > 0 && Number(r.closing) <= Number(r.opening)) && (
              <tr><td colSpan={9} className="text-xs text-red-700 bg-red-50 border-l-4 border-red-500 p-2">
                Closing Reading cannot be less than or equal to the Opening Reading. If the nozzle is not functional then do not select or put readings of that nozzle.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AdjustmentsSection({ adjustments, setAdjustments, nozzles, prices }) {
  const add = () => setAdjustments([...adjustments, { _uid: rowUid(), type: "Steel Can Trial", fuel: "MS", nozzle_id: "", deduction_litres: 0, price: prices.MS, reason: "" }]);
  const upd = (i, p) => { const n = [...adjustments]; n[i] = { ...n[i], ...p }; if (p.fuel) n[i].price = prices[p.fuel]; setAdjustments(n); };
  const del = (i) => setAdjustments(adjustments.filter((_, j) => j !== i));

  // v17.11 (2026-08-19) — brief bug 1: Steel Can Trial and Excess Trial are
  // CALIBRATION events, not sales. Their ₹ column MUST render blank. Only
  // Meter Jumping and Short Collection have real cash impact.
  const isTrial = (t) => t === "Steel Can Trial" || t === "Excess Trial";

  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-800">Nozzle Adjustments</h3>
        <Button data-testid="add-adj-btn" type="button" variant="outline" size="sm" onClick={add} className="border-slate-300"><Plus size={14} /> Add</Button>
      </div>
      <div className="text-[10px] text-slate-500 mb-2">
        Steel Can Trial / Excess Trial = calibration only. They subtract litres from the nozzle&rsquo;s Net (L) but never add rupees. Only Meter Jumping and Short Collection carry a rupee value.
      </div>
      <div className="overflow-x-auto">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Type</th><th>Fuel</th><th>Nozzle</th><th>Litres</th><th>₹/unit</th><th>Total ₹</th><th>Reason</th><th></th></tr></thead>
          <tbody>
            {adjustments.map((a, i) => {
              const trial = isTrial(a.type);
              return (
              <tr key={a._uid || `${a.nozzle_id || "empty"}-${i}`}>
                <td>
                  <select value={a.type} onChange={(e) => upd(i, { type: e.target.value })} className="border border-slate-300 rounded-sm p-1" data-testid={`adj-type-${i}`}>
                    {["Steel Can Trial","Meter Jumping","Excess Trial","Short Collection"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </td>
                <td>
                  <select value={a.fuel} onChange={(e) => upd(i, { fuel: e.target.value })} className="border border-slate-300 rounded-sm p-1">
                    {["HSD","MS","CNG"].map((f) => <option key={f}>{f}</option>)}
                  </select>
                </td>
                <td>
                  <select value={a.nozzle_id || ""} onChange={(e) => upd(i, { nozzle_id: e.target.value })} className="border border-slate-300 rounded-sm p-1 w-40">
                    <option value="">—</option>
                    {nozzles.filter((n) => n.fuel === a.fuel).map((n) => <option key={n.nozzle_id} value={n.nozzle_id}>{n.label}</option>)}
                  </select>
                </td>
                <td><Input type="number" step="0.01" value={a.deduction_litres} onChange={(e) => upd(i, { deduction_litres: Number(e.target.value) })} className="num w-24 h-9 border border-slate-300" /></td>
                <td>
                  {trial ? (
                    <span className="text-slate-400" data-testid={`adj-price-blank-${i}`}>—</span>
                  ) : (
                    <Input type="number" step="0.01" value={a.price} onChange={(e) => upd(i, { price: Number(e.target.value) })} className="num w-20 h-9 border border-slate-300" />
                  )}
                </td>
                <td className="num font-bold" data-testid={`adj-total-${i}`}>
                  {trial ? (
                    <span className="text-slate-400 font-normal" title="Trials are calibration events — no rupee value.">—</span>
                  ) : fmtINR(Number(a.deduction_litres) * Number(a.price))}
                </td>
                <td><Input value={a.reason || ""} onChange={(e) => upd(i, { reason: e.target.value })} className="h-9 border border-slate-300" /></td>
                <td><button type="button" onClick={() => del(i)} className="text-red-600"><Trash2 size={14} /></button></td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function LubricantsSection({ lubSales, setLubSales, lubs }) {
  const add = () => setLubSales([...lubSales, { _uid: rowUid(), lub_id: "", qty: 1, purchase_price: 0, selling_price: 0 }]);
  const upd = (i, p) => {
    const n = [...lubSales]; n[i] = { ...n[i], ...p };
    if (p.lub_id) {
      const l = lubs.find((x) => x.lub_id === p.lub_id);
      if (l) { n[i].purchase_price = l.purchase_price || 0; n[i].selling_price = l.sale_price || 0; }
    }
    setLubSales(n);
  };
  const del = (i) => setLubSales(lubSales.filter((_, j) => j !== i));

  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="font-bold text-slate-800">Lubricants Sales</h3>
        <div className="flex gap-2">
          <Button data-testid="add-lub-btn" type="button" variant="outline" size="sm" onClick={add} className="border-slate-300"><Plus size={14} /> Add</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Product</th><th>Qty</th><th>Cost ₹</th><th>Sell ₹</th><th>Total ₹</th><th></th></tr></thead>
          <tbody>
            {lubSales.map((l, i) => (
              <tr key={l._uid || `${l.lub_id || "empty"}-${i}`}>
                <td>
                  <select value={l.lub_id} onChange={(e) => upd(i, { lub_id: e.target.value })} className="border border-slate-300 rounded-sm p-1 w-64">
                    <option value="">—</option>
                    {lubs.map((x) => <option key={x.lub_id} value={x.lub_id}>{x.name}</option>)}
                  </select>
                </td>
                <td><Input type="number" step="1" value={l.qty} onChange={(e) => upd(i, { qty: Number(e.target.value) })} className="num w-16 h-9 border border-slate-300" /></td>
                <td><Input type="number" step="0.01" value={l.purchase_price} onChange={(e) => upd(i, { purchase_price: Number(e.target.value) })} className="num w-20 h-9 border border-slate-300" /></td>
                <td><Input type="number" step="0.01" value={l.selling_price} onChange={(e) => upd(i, { selling_price: Number(e.target.value) })} className="num w-20 h-9 border border-slate-300" /></td>
                <td className="num font-bold">{fmtINR(Number(l.qty) * Number(l.selling_price))}</td>
                <td><button type="button" onClick={() => del(i)} className="text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PaymentsSection({ payments, setPayments, pms, currentUserEmail }) {
  const upd = (i, p) => { const n = [...payments]; n[i] = { ...n[i], ...p }; setPayments(n); };

  // Ensure every active PM × both categories is present at ₹0 (idempotent).
  useEffect(() => {
    if (!pms.length) return;
    setPayments((cur) => {
      const has = new Set(cur.map((r) => `${r.pm_id}|${r.category}`));
      const missing = [];
      for (const pm of pms) {
        for (const cat of ["Fuels", "CNG"]) {
          const key = `${pm.pm_id}|${cat}`;
          if (!has.has(key)) missing.push({
            pm_id: pm.pm_id, category: cat, amount: 0,
            misc_customer_name: "", payment_done_through: "",
            nil: false, nil_marked_by: null, nil_marked_at: null,
            touched: false,
          });
        }
      }
      return missing.length ? [...cur, ...missing] : cur;
    });
  }, [pms, setPayments]);

  const toggleNil = (i, row) => {
    if (row.nil) {
      // Un-mark
      upd(i, { nil: false, nil_marked_by: null, nil_marked_at: null, touched: true });
    } else {
      upd(i, {
        nil: true, amount: 0, touched: true,
        nil_marked_by: currentUserEmail || null,
        nil_marked_at: new Date().toISOString(),
      });
    }
  };

  // Group by mode for a cleaner view — one card per payment mode with two rows (Fuels / CNG)
  const byMode = useMemo(() => {
    const g = new Map();
    payments.forEach((p, idx) => {
      if (!g.has(p.pm_id)) g.set(p.pm_id, {});
      g.get(p.pm_id)[p.category] = { ...p, _idx: idx };
    });
    return g;
  }, [payments]);

  const modeCount = pms.length;

  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div>
          <h3 className="font-bold text-slate-800">Payments · every mode required</h3>
          <p className="text-xs text-slate-600 mt-0.5" data-testid="payments-helper-text">
            Every mode must be filled in or marked Nil. Proof slips are uploaded later on the Upload page.
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">{modeCount} active modes configured.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {pms.map((pm) => {
          const rows = byMode.get(pm.pm_id) || {};
          const totalForMode = ["Fuels", "CNG"].reduce((s, c) => {
            const r = rows[c];
            return s + (r?.nil ? 0 : (Number(r?.amount) || 0));
          }, 0);
          return (
            <div key={pm.pm_id} className="border-2 border-slate-200 rounded-sm p-2 bg-white" data-testid={`pm-card-${pm.pm_id}`}>
              {/* Tile header — mode name + running total. No image. */}
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold text-sm text-slate-900 truncate">{pm.name}</div>
                <div className="text-xs num font-bold text-slate-700 shrink-0">{fmtINR(totalForMode)}</div>
              </div>
              {["Fuels", "CNG"].map((cat) => {
                const r = rows[cat];
                if (!r) return null;
                const i = r._idx;
                const isMisc = pm.name === "Miscellaneous";
                return (
                  <div key={cat} className="flex items-center gap-2 py-1 border-t border-slate-100" data-testid={`pm-row-${pm.pm_id}-${cat}`}>
                    <span className="text-xs font-bold text-slate-500 w-10 shrink-0">{cat === "Fuels" ? "Fuel" : "CNG"}</span>
                    {r.nil ? (
                      <div
                        className="num h-9 border border-slate-300 bg-slate-100 text-slate-500 font-bold flex-1 min-w-[80px] flex items-center px-3 rounded-sm"
                        data-testid={`pm-amount-${pm.pm_id}-${cat}`}
                      >
                        Nil
                      </div>
                    ) : (
                      <Input
                        type="number" step="0.01" value={r.amount}
                        onChange={(e) => upd(i, { amount: Number(e.target.value), touched: true })}
                        className="num h-9 border border-slate-300 flex-1 min-w-[80px]"
                        data-testid={`pm-amount-${pm.pm_id}-${cat}`}
                      />
                    )}
                    {isMisc && !r.nil && r.amount > 0 && (
                      <Input placeholder="Customer" value={r.misc_customer_name || ""} onChange={(e) => upd(i, { misc_customer_name: e.target.value, touched: true })} className="h-9 border border-slate-300 w-28" />
                    )}
                    {/* Mark-as-Nil toggle — plain English, replaces the old "NO TXN".
                        A blank field is NOT the same as Nil. */}
                    <button
                      type="button"
                      onClick={() => toggleNil(i, r)}
                      className={
                        "shrink-0 text-[10px] font-bold px-2 py-1 rounded-sm border transition-colors " +
                        (r.nil
                          ? "bg-slate-800 border-slate-800 text-white"
                          : "bg-white border-slate-400 text-slate-700 hover:bg-slate-50")
                      }
                      title={r.nil
                        ? "This row is marked Nil. Click to un-mark."
                        : "No transactions happened in this mode this shift. Marking Nil is a positive statement — a blank field is not the same as Nil."
                      }
                      data-testid={`pm-nil-btn-${pm.pm_id}-${cat}`}
                    >
                      {r.nil ? "Nil ✓" : "Mark as Nil"}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DebtorsSection({ debtorRows, setDebtorRows, debtors, prices, date, shiftCode }) {
  const add = () => setDebtorRows([...debtorRows, { _uid: rowUid(), debtor_id: debtors[0]?.debtor_id || "", fuel: "HSD", amount_rs: 0, qty: 0, vehicle_no: "", driver_name: "", slip_no: "", reason: "", proof_file_id: null }]);
  const upd = (i, p) => {
    const n = [...debtorRows]; n[i] = { ...n[i], ...p };
    if (p.amount_rs != null && !p.qty && n[i].fuel && prices[n[i].fuel]) n[i].qty = Number((Number(p.amount_rs) / prices[n[i].fuel]).toFixed(2));
    setDebtorRows(n);
  };
  const del = (i) => setDebtorRows(debtorRows.filter((_, j) => j !== i));

  const uploadSlip = async (i, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (date) fd.append("date", date);
      if (shiftCode) fd.append("shift_code", shiftCode);
      fd.append("note", `Debtor signed slip — row ${i + 1}`);
      const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(i, { proof_file_id: r.data.file_id, proof_name: r.data.original_name });
      toast.success("Signed slip attached");
    } catch (e) { toast.error(apiError(e)); }
  };

  const REASONS = ["PO", "Fleet card down", "Site refuel", "Emergency", "Standing arrangement", "Other"];
  // v17.11 (2026-08-19) — bug 7: plain-English explanation of each option.
  const REASON_HINTS = {
    "PO": "Fuel given against a purchase order this debtor pre-arranged with us.",
    "Fleet card down": "Their usual card/QR (e.g. Xtrapower) wasn't working — billed on credit instead.",
    "Site refuel": "Vehicle refuelled off-site or on a standing job (construction, etc.), not walk-in.",
    "Emergency": "Customer had no means to pay at the moment — goodwill / urgent credit.",
    "Standing arrangement": "Long-term running credit account with us (e.g. Bhoir, Vaishnavi Traders).",
    "Other": "Anything not covered above — a free-text reason note becomes mandatory.",
  };

  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-800">Debtor Entries <span className="text-xs font-normal text-red-700">· signed slip photo + reason are mandatory</span></h3>
        <Button data-testid="add-debtor-btn" type="button" variant="outline" size="sm" onClick={add} className="border-slate-300"><Plus size={14} /> Add</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Debtor</th><th>Fuel</th><th>Amount ₹</th><th>Qty</th><th>Vehicle No.</th><th>Driver</th><th>Slip #</th><th>Reason</th><th>Signed slip</th><th></th></tr></thead>
          <tbody>
            {debtorRows.map((d, i) => (
              <tr key={d._uid || `${d.debtor_id || "empty"}-${i}`}>
                <td>
                  <select value={d.debtor_id} onChange={(e) => upd(i, { debtor_id: e.target.value })} className="border border-slate-300 rounded-sm p-1 w-52" data-testid={`debtor-select-${i}`}>
                    {debtors.map((x) => <option key={x.debtor_id} value={x.debtor_id}>{x.name}</option>)}
                  </select>
                </td>
                <td>
                  <div className="flex gap-1">
                    {["HSD","MS","CNG"].map((f) => (
                      <button key={f} type="button" onClick={() => upd(i, { fuel: f })} className={"px-2 py-1 border rounded-sm text-xs font-bold " + (d.fuel === f ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")}>{f}</button>
                    ))}
                  </div>
                </td>
                <td><Input type="number" step="0.01" value={d.amount_rs} onChange={(e) => upd(i, { amount_rs: Number(e.target.value) })} className="num w-28 h-9 border border-slate-300" /></td>
                <td><Input type="number" step="0.01" value={d.qty} onChange={(e) => upd(i, { qty: Number(e.target.value) })} className="num w-24 h-9 border border-slate-300" /></td>
                <td><Input value={d.vehicle_no || ""} onChange={(e) => upd(i, { vehicle_no: e.target.value.toUpperCase() })} placeholder="MH-04-XX-1234" className="w-32 h-9 border border-slate-300 uppercase" data-testid={`debtor-vehicle-${i}`} /></td>
                <td><Input value={d.driver_name || ""} onChange={(e) => upd(i, { driver_name: e.target.value })} placeholder="Driver" className="w-28 h-9 border border-slate-300" /></td>
                <td><Input value={d.slip_no || ""} onChange={(e) => upd(i, { slip_no: e.target.value })} placeholder="Slip #" className="w-24 h-9 border border-slate-300" /></td>
                <td>
                  <select value={d.reason || ""} onChange={(e) => upd(i, { reason: e.target.value })}
                          className={"border rounded-sm p-1 w-28 " + ((Number(d.amount_rs) > 0 && !(d.reason || "").trim()) ? "border-red-500 bg-red-50" : "border-slate-300")}
                          title={REASON_HINTS[d.reason] || "Pick why this fuel is on credit."}
                          data-testid={`debtor-reason-${i}`}>
                    <option value="">— Reason * —</option>
                    {REASONS.map((r) => <option key={r} value={r} title={REASON_HINTS[r]}>{r}</option>)}
                  </select>
                  {d.reason === "Other" && (
                    <Input value={d.other_note || ""} onChange={(e) => upd(i, { other_note: e.target.value })}
                           placeholder="Say why (required)" data-testid={`debtor-other-note-${i}`}
                           className={"w-28 h-8 mt-1 border " + (!(d.other_note || "").trim() ? "border-red-500 bg-red-50" : "border-slate-300")} />
                  )}
                  {d.reason && REASON_HINTS[d.reason] && (
                    <div className="text-[10px] text-slate-500 mt-0.5 max-w-[7rem]" data-testid={`debtor-reason-hint-${i}`}>
                      {REASON_HINTS[d.reason]}
                    </div>
                  )}
                </td>
                <td>
                  {d.proof_file_id ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 bg-emerald-50 border border-emerald-400 rounded-sm text-emerald-800" data-testid={`debtor-slip-ok-${i}`}>
                      <Check size={10} /> On Upload
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-1 bg-amber-50 border border-amber-400 rounded-sm text-amber-800" data-testid={`debtor-slip-pending-${i}`}>
                      Attach in Upload
                    </span>
                  )}
                </td>
                <td><button type="button" onClick={() => del(i)} className="text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2">Vehicle No. is optional but recommended for B2B credit customers (Afcons, Bhoir, etc.). <b>Signed slip</b> photo is attached later on the Upload screen — Shift Entry is typing-only.</p>
    </Card>
  );
}

function CashBlock({ hasFuels, hasCng, preview, hints, cashFuels, setCashFuels, cashCng, setCashCng, cashTol, varianceFuels, setVarianceFuels, varianceCng, setVarianceCng }) {
  const derivedFuels = preview?.cash_derived_fuels || 0;
  const derivedCng = preview?.cash_derived_cng || 0;
  const diffFuels = preview?.difference_fuels || 0;
  const diffCng = preview?.difference_cng || 0;
  const tol = Number(cashTol) || 10;

  // Phase-3 A1 reason picklist. NOT reused from the Miscellaneous payment
  // mode — these two are different concepts:
  //   * Miscellaneous payment mode = a payment actually received by some
  //     other channel.
  //   * Cash variance                = cash that didn't add up. Recorded
  //     against the shift, kept visible as Difference, flagged always.
  const REASONS = [
    "Rounding",
    "Short collection",
    "Customer drive-off",
    "Change given",
    "Error in reading",
    "Other",
  ];

  const renderExplained = (diff, variance) => {
    if (Math.abs(diff) < 0.01) return null;
    if (!variance?.reason_code) return null;
    return (
      <div className="text-xs text-amber-900 mt-1" data-testid="variance-explained">
        Difference {fmtINR(diff)} — explained: <b>{variance.reason_code}</b>
        {variance.note ? <> · <span className="text-slate-700">{variance.note}</span></> : null}
      </div>
    );
  };

  const renderVariancePicker = (diff, variance, setVariance, testKey) => {
    if (Math.abs(diff) <= tol && !variance?.reason_code) {
      // Within tolerance — will be auto-recorded as "Rounding" on the backend.
      return (
        <div className="mt-2 p-2 border border-slate-300 bg-slate-50 rounded-sm text-xs text-slate-600" data-testid={`variance-within-tol-${testKey}`}>
          Within tolerance (±₹{tol}). This will be auto-recorded as <b>Rounding</b> — no reason needed.
        </div>
      );
    }
    return (
      <div className="mt-3 p-2 border-2 border-amber-500 bg-amber-50 rounded-sm" data-testid={`variance-block-${testKey}`}>
        <div className="text-xs font-bold text-amber-900 mb-1 uppercase tracking-wider">Explain this variance</div>
        <p className="text-xs text-amber-800 mb-2">
          This ₹{fmtNum(diff)} gap will be recorded as a cash variance
          against you (attribution stays on your name). It stays visible on
          the shift — a reason explains it but doesn&rsquo;t make it disappear.
        </p>
        <select
          value={variance?.reason_code || ""}
          onChange={(e) => setVariance({ ...variance, reason_code: e.target.value })}
          className="w-full h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm"
          data-testid={`variance-reason-${testKey}`}
        >
          <option value="">— Reason —</option>
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {variance?.reason_code === "Other" && (
          <p className="text-[11px] text-red-800 mt-1">A note is required when reason is <b>Other</b>.</p>
        )}
        <Input
          placeholder={variance?.reason_code === "Other" ? "Describe the gap (required)" : "Optional note"}
          value={variance?.note || ""}
          onChange={(e) => setVariance({ ...variance, note: e.target.value })}
          className="h-9 mt-1 border border-slate-300"
          data-testid={`variance-note-${testKey}`}
        />
      </div>
    );
  };

  return (
    <Card className="p-4 border-2 border-slate-900 rounded-sm bg-slate-50" data-testid="cash-reconciliation">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-bold text-slate-900">Cash Reconciliation</h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Total Sales − Digital Payments − Debtor Credit + Adjustments = Expected Cash. Enter Cash in Hand → Difference computes live. <b>Tolerance ±₹{tol}</b> — beyond that, pick a reason to route it to a variance record (never zeroes out).
          </p>
        </div>
      </div>

      {/* Live equation — always visible so the cashier sees the math. */}
      {preview && (
        <div className="mb-3 p-3 bg-white border border-slate-300 rounded-sm text-xs font-mono text-slate-700 overflow-x-auto" data-testid="live-cash-equation">
          {hasFuels && (
            <>
              <div>Sales (Fuels + Lubricants)  = ₹{fmtNum((preview.total_sales_fuels || 0) + (preview.lubricant_total || 0))}</div>
              <div>&nbsp;&nbsp;− Digital pay (Fuels)     = ₹{fmtNum((preview.total_sales_fuels || 0) + (preview.lubricant_total || 0) - (preview.cash_derived_fuels || 0) - 0)}</div>
              <div>= Expected Cash (Fuels)     <b>= ₹{fmtNum(derivedFuels)}</b></div>
            </>
          )}
          {hasCng && (
            <div className="mt-1 pt-1 border-t border-slate-200">
              Expected Cash (CNG) = <b>₹{fmtNum(derivedCng)}</b>
            </div>
          )}
        </div>
      )}

      <div className={"grid gap-4 " + (hasFuels && hasCng ? "md:grid-cols-2" : "")}>
        {hasFuels && (
          <div className="bg-white p-3 border-2 border-slate-300 rounded-sm">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Fuels (HSD + MS)</div>
            <div className="mt-2">
              <div className="text-xs text-slate-500">Expected Cash (₹)</div>
              <div className="num text-lg font-bold" data-testid="cash-derived-fuels">{fmtINR(derivedFuels)}</div>
            </div>
            <div className="mt-2">
              <div className="text-xs text-slate-500">Cash in Hand (₹) *</div>
              <Input data-testid="cash-in-hand-fuels" type="number" step="0.01" value={cashFuels} onChange={(e) => setCashFuels(e.target.value)} className="num h-11 border border-slate-300 mt-1 text-lg" />
            </div>
            <div className="mt-2">
              <div className="text-xs text-slate-500">Difference (₹)</div>
              <div data-testid="diff-fuels" className={"num text-2xl font-bold " + (Math.abs(diffFuels) > tol ? "text-red-600" : (Math.abs(diffFuels) < 0.01 ? "text-emerald-700" : "text-amber-700"))}>{fmtINR(diffFuels)}</div>
              {renderExplained(diffFuels, varianceFuels)}
              {hints?.fuels && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 p-2 rounded-sm mt-1">{hints.fuels}</div>}
            </div>
            {Math.abs(diffFuels) > 0.01 && renderVariancePicker(diffFuels, varianceFuels, setVarianceFuels, "fuels")}
          </div>
        )}
        {hasCng && (
          <div className="bg-white p-3 border-2 border-slate-300 rounded-sm">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600">CNG</div>
            <div className="mt-2">
              <div className="text-xs text-slate-500">Expected Cash (₹)</div>
              <div className="num text-lg font-bold" data-testid="cash-derived-cng">{fmtINR(derivedCng)}</div>
            </div>
            <div className="mt-2">
              <div className="text-xs text-slate-500">Cash in Hand (₹) *</div>
              <Input data-testid="cash-in-hand-cng" type="number" step="0.01" value={cashCng} onChange={(e) => setCashCng(e.target.value)} className="num h-11 border border-slate-300 mt-1 text-lg" />
            </div>
            <div className="mt-2">
              <div className="text-xs text-slate-500">Difference (₹)</div>
              <div data-testid="diff-cng" className={"num text-2xl font-bold " + (Math.abs(diffCng) > tol ? "text-red-600" : (Math.abs(diffCng) < 0.01 ? "text-emerald-700" : "text-amber-700"))}>{fmtINR(diffCng)}</div>
              {renderExplained(diffCng, varianceCng)}
              {hints?.cng && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 p-2 rounded-sm mt-1">{hints.cng}</div>}
            </div>
            {Math.abs(diffCng) > 0.01 && renderVariancePicker(diffCng, varianceCng, setVarianceCng, "cng")}
          </div>
        )}
      </div>
    </Card>
  );
}

// -------- Sanitizers (numbers) --------
function ReadyToSubmit({ readiness, setPage }) {
  if (!readiness) {
    return (
      <Card className="p-3 border-2 border-slate-300 rounded-sm bg-slate-50" data-testid="ready-panel-loading">
        <div className="text-xs text-slate-500">Checking what&rsquo;s still needed…</div>
      </Card>
    );
  }

  const CATEGORIES = [
    { key: "basics",      label: "Basics",       page: 1 },
    { key: "readings",    label: "Nozzle readings", page: 2 },
    { key: "adjustments", label: "Adjustments",  page: 2 },
    { key: "payments",    label: "Payment proofs", page: 3 },
    { key: "debtors",     label: "Debtor slips", page: 3 },
    { key: "cash",        label: "Cash reconciliation", page: 3 },
    { key: "receipts",    label: "Receipts (optional)", page: 3 },
  ];

  const grouped = {};
  for (const r of readiness.reasons || []) {
    (grouped[r.category] ||= []).push(r);
  }
  for (const t of readiness.todo || []) {
    (grouped[t.category] ||= []).push({ ...t, optional: true });
  }

  const isReady = readiness.ready;
  const totalMissing = (readiness.reasons || []).length;

  return (
    <Card
      className={"p-4 border-2 rounded-sm " + (isReady ? "border-emerald-600 bg-emerald-50" : "border-amber-500 bg-amber-50")}
      data-testid="ready-panel"
    >
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div>
          <h3 className={"font-bold text-lg " + (isReady ? "text-emerald-800" : "text-amber-900")}>
            {isReady ? "Ready to submit ✓" : `${totalMissing} thing${totalMissing === 1 ? "" : "s"} still to fill`}
          </h3>
          <p className="text-xs text-slate-700 mt-0.5">
            {isReady
              ? "Everything looks complete. Tap Submit Entry when you're done."
              : "Cross off each item below. Submit unlocks when all are done. If a shift genuinely had none of a thing (e.g. no credit sales), just leave that section empty — this panel won't nag you."}
          </p>
        </div>
        <div className={"text-xs font-mono font-bold px-2 py-1 rounded-sm " +
          (isReady ? "bg-emerald-600 text-white" : "bg-amber-500 text-white")}>
          {isReady ? "OK" : `${totalMissing} left`}
        </div>
      </div>

      {!isReady && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          {CATEGORIES.map((c) => {
            const items = grouped[c.key];
            if (!items || !items.length) return null;
            return (
              <div key={c.key} className="bg-white border-2 border-slate-300 rounded-sm p-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700">{c.label}</div>
                  {c.page && setPage && (
                    <button type="button" onClick={() => setPage(c.page)}
                            className="text-[10px] font-bold px-1.5 py-0.5 border border-slate-400 rounded-sm hover:bg-slate-100"
                            data-testid={`ready-jump-${c.key}`}>
                      Jump to page {c.page}
                    </button>
                  )}
                </div>
                <ul className="space-y-1">
                  {items.map((it, i) => (
                    <li key={`${c.key}-${it.label}-${i}`} className="text-sm text-slate-800 flex items-start gap-1.5" data-testid={`ready-item-${c.key}-${i}`}>
                      <span className={it.optional ? "text-slate-400" : "text-red-500"}>{it.optional ? "○" : "✗"}</span>
                      <span>
                        <b>{it.label}.</b> <span className="text-slate-600">{it.detail}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// Cheap unique id for a row — used as React `key` so mid-list deletes don't
// corrupt DOM identity (previously we used array index). Not cryptographic.
function rowUid() { return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; }

function sanitizeReading(r) {
  return {
    nozzle_id: r.nozzle_id,
    opening: Number(r.opening) || 0,
    closing: Number(r.closing) || 0,
    trial_litres: Number(r.trial_litres) || 0,
    price: Number(r.price) || 0,
    override_meter_rollover: !!r.override_meter_rollover,
    opening_reason: r.opening_reason || null,
    trial_reason: r.trial_reason || null,
  };
}
function sanitizeAdj(a) {
  return {
    type: a.type, fuel: a.fuel, nozzle_id: a.nozzle_id || null,
    deduction_litres: Number(a.deduction_litres) || 0, price: Number(a.price) || 0,
    reason: a.reason || "", proof_file_id: a.proof_file_id || null,
  };
}
function sanitizeLub(l) {
  return {
    lub_id: l.lub_id,
    qty: Number(l.qty) || 0,
    purchase_price: Number(l.purchase_price) || 0,
    selling_price: Number(l.selling_price) || 0,
  };
}
function sanitizePay(p) {
  return {
    pm_id: p.pm_id, category: p.category, amount: Number(p.amount) || 0,
    misc_customer_name: p.misc_customer_name || "",
    payment_done_through: p.payment_done_through || "",
    proof_file_id: p.proof_file_id || null,
    // misc_adjustment_* were the old Phase-3 A1 storage — now handled by
    // the separate `variance_fuels` / `variance_cng` fields on the entry.
    // Kept null here so any stale draft doesn't fail server validation.
    misc_adjustment_reason: null,
    misc_adjustment_note: "",
  };
}

// Phase-3 A1: only send a variance record when there IS a real diff.  If
// |diff| < 1 paisa we send null so the backend clears any stale value.
function sanitizeVariance(v, diff) {
  if (!diff || Math.abs(Number(diff) || 0) < 0.01) return null;
  return {
    amount: Number(diff) || 0,
    reason_code: (v?.reason_code || "").trim() || null,
    note: (v?.note || "").trim(),
  };
}
function sanitizeDeb(d) {
  return {
    debtor_id: d.debtor_id, fuel: d.fuel,
    amount_rs: Number(d.amount_rs) || 0, qty: Number(d.qty) || 0,
    vehicle_no: (d.vehicle_no || "").trim().toUpperCase(),
    driver_name: (d.driver_name || "").trim(),
    slip_no: (d.slip_no || "").trim(),
    // Phase-3 §2: signed-receipt & reason.
    reason: d.reason || "",
    proof_file_id: d.proof_file_id || null,
  };
}
